import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  ClientContextBlocksResponse,
  ClientContextEntry,
  ContextBlock,
  ContextBlocksResponse,
  ContextEntry,
  ContextEntryDraft,
  ContextSubjectKind,
  StatementSearchResponse,
} from './context-blocks.types';

/**
 * The context blocks' Ops routes (FLOW-389, FLOW-376). Every write is a named
 * act and append-only: there is no edit, no delete, no PATCH anywhere here, and
 * none should be added (the `context-blocks` skill). Ops-only (N4) — `opsGuard`
 * keeps the callers off everyone else's UI.
 */
@Injectable({ providedIn: 'root' })
export class ContextBlocksService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/context/ops`;

  /** The journey's blocks, seeding the defaults on first read. */
  list(kind: ContextSubjectKind, shortId: string): Observable<ContextBlocksResponse> {
    return this.http.get<ContextBlocksResponse>(`${this.base}/${kind}/${shortId}/blocks/`);
  }

  /** *Add a block* — from the starting list, or `OTHER` named in Ops's words. */
  addBlock(
    kind: ContextSubjectKind,
    shortId: string,
    payload: { kind: string; name?: string; subject_key?: string },
  ): Observable<ContextBlock> {
    return this.http.post<ContextBlock>(`${this.base}/${kind}/${shortId}/blocks/`, payload);
  }

  /** *Add to this* — text and/or a document, with where it came from. */
  addEntry(
    kind: ContextSubjectKind,
    shortId: string,
    blockShortId: string,
    draft: ContextEntryDraft,
  ): Observable<ContextEntry> {
    const fd = new FormData();
    for (const [key, value] of Object.entries(draft)) {
      // A `false` statement choice is an answer and is sent (FLOW-350 part 2).
      if (typeof value === 'boolean') fd.append(key, String(value));
      else if (value) fd.append(key, value);
    }
    return this.http.post<ContextEntry>(
      `${this.base}/${kind}/${shortId}/blocks/${blockShortId}/entries/`,
      fd,
    );
  }

  /** *Remove block* — the one soft delete, of an empty block only (`block.removable`). */
  removeBlock(kind: ContextSubjectKind, shortId: string, blockShortId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${kind}/${shortId}/blocks/${blockShortId}/`);
  }

  /** *No longer needed* — collapses the block with a reason; removes nothing. */
  retire(
    kind: ContextSubjectKind,
    shortId: string,
    blockShortId: string,
    reason: string,
  ): Observable<ContextBlock> {
    return this.http.post<ContextBlock>(
      `${this.base}/${kind}/${shortId}/blocks/${blockShortId}/no-longer-needed/`,
      { reason },
    );
  }

  /** The application's one order of its stack — every Ops viewer sees it. */
  setOrder(
    kind: ContextSubjectKind,
    shortId: string,
    order: string[],
  ): Observable<{ order: string[] }> {
    return this.http.put<{ order: string[] }>(`${this.base}/${kind}/${shortId}/order/`, {
      order,
    });
  }

  /** The file as filed; the server records DOWNLOADED, or OPENED with `open`. */
  entryFile(
    kind: ContextSubjectKind,
    shortId: string,
    blockShortId: string,
    entryShortId: string,
    open: boolean,
  ): Observable<Blob> {
    const params = open ? new HttpParams().set('open', '1') : undefined;
    return this.http.get(
      `${this.base}/${kind}/${shortId}/blocks/${blockShortId}/entries/${entryShortId}/file/`,
      { params, responseType: 'blob' },
    );
  }

  /** The statements and their parsed lines, for Ops to search. */
  statements(kind: ContextSubjectKind, shortId: string): Observable<StatementSearchResponse> {
    return this.http.get<StatementSearchResponse>(`${this.base}/${kind}/${shortId}/statements/`);
  }

  /** Every statement as filed, in one .zip; the server records each as downloaded. */
  statementsZip(kind: ContextSubjectKind, shortId: string): Observable<Blob> {
    return this.http.get(`${this.base}/${kind}/${shortId}/statements/zip/`, {
      responseType: 'blob',
    });
  }

  // -------------------------------------------------------------------------
  // The business's and agent's routes (FLOW-391) — shared blocks only, scoped
  // by membership or agency on the server. Where an entry came from is the
  // system's to record, never the business's to choose.
  // -------------------------------------------------------------------------

  private readonly clientBase = `${environment.apiUrl}/api/context`;

  clientList(kind: ContextSubjectKind, shortId: string): Observable<ClientContextBlocksResponse> {
    return this.http.get<ClientContextBlocksResponse>(
      `${this.clientBase}/${kind}/${shortId}/blocks/`,
    );
  }

  clientAddEntry(
    kind: ContextSubjectKind,
    shortId: string,
    blockShortId: string,
    draft: { text?: string; file?: File },
  ): Observable<ClientContextEntry> {
    const fd = new FormData();
    if (draft.text) fd.append('text', draft.text);
    if (draft.file) fd.append('file', draft.file);
    return this.http.post<ClientContextEntry>(
      `${this.clientBase}/${kind}/${shortId}/blocks/${blockShortId}/entries/`,
      fd,
    );
  }

  clientEntryFile(
    kind: ContextSubjectKind,
    shortId: string,
    blockShortId: string,
    entryShortId: string,
  ): Observable<Blob> {
    return this.http.get(
      `${this.clientBase}/${kind}/${shortId}/blocks/${blockShortId}/entries/${entryShortId}/file/`,
      { params: new HttpParams().set('open', '1'), responseType: 'blob' },
    );
  }
}
